import React, { useContext, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { UncontrolledTooltip } from 'reactstrap';
import missionReducer from './missionReducer';
import {
  Button,
  ConfirmModal,
  CustomModal,
  LinkButton,
  SpinnerArea,
  Table,
  TablePagination,
} from '../../../shared/components';
import operationService from '../../../services/operationService';
import missionsService from '../../../services/missionsService';
import useServiceReducer from '../../../shared/hooks/useServiceReducer';
import { MissionFilter, OperationMission, QueryGroup } from '../../../services/types/Operation';
import { Cell } from '../../../shared/components/Table';
import {
  customDateStr,
  isDraft,
  tooltipPreventOverflowModifiers,
} from '../../../shared/misc/utils';
import OperationContext from './context';
import Context from '../../App/MainProvider/context';
import MissionForm from './MissionForm';
import { CreateMission, Mission, OperationMissionForm } from '../../../services/types/Missions';
import Role from '../../../types/role';
import { User } from '../../../services/types/User';
import MissionStatus from './MissionStatus';
import MissionType from './MissionType';

const Missions: React.FC = () => {
  const { t } = useTranslation();
  const { user, inSuperUserMode } = useContext(Context);
  const {
    operation,
    operation: { id, number, isMember, groupCode },
  } = useContext(OperationContext);
  const { service, notify } = useContext(Context);

  const [showCreateNewMissionModal, setShowCreateNewMissionModal] = useState(false);
  const [missionState, setMissionState] = useState<OperationMissionForm | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canExecute =
    (isMember ||
      inSuperUserMode() ||
      user.isHso ||
      (user.roleCode === Role.GroupHead && user.groupCode === groupCode)) &&
    !isDraft(operation);

  const initFilter: MissionFilter = {
    operationId: id,
    pageNumber: 1,
    sortField: 'Default',
    sortDescending: true,
    queryGroup: QueryGroup.All,
  };

  const [data, loading, filter, dispatch, loader, reload] = useServiceReducer(
    operationService.getMissions,
    missionReducer,
    initFilter
  );

  const pageSize = 8;
  const changePage = (page: number) => dispatch({ type: 'PAGE', value: page });
  const changeSort = (column: string, descending: boolean) =>
    dispatch({ type: 'ORDER', column, descending });

  const openMissionFormModal = () => setShowCreateNewMissionModal(true);
  const closeMissionFormModal = () => setShowCreateNewMissionModal(false);
  const handleShowCreateNewMissionModal = () => {
    service(() => missionsService.getNextNumber(), {
      callback: (response) => {
        const state: OperationMissionForm = {
          missionNumber: response,
          missionId: undefined,
          startDate: null,
          endDate: null,
          country: '',
          destinationCountry: '',
          objective: '',
          members: [],
        };

        setMissionState(state);
        openMissionFormModal();
      },
    });
  };

  const handleMissionFormSubmit = (values: OperationMissionForm, resetFormCallback: () => void) => {
    const { startDate, endDate, country, destinationCountry, objective, members } = values;
    const payload: CreateMission = {
      operationId: id,
      type: '',
      startDate: startDate || new Date(),
      endDate: endDate || new Date(),
      country,
      destinationCountry,
      objective,
      membersEmails: members.map(({ email }) => email),
    };
    // Update task flow
    if (missionState) {
      payload.id = missionState.missionId;
    }
    const notifyTitle = payload.id ? 'Update Mission' : 'Create New Mission';
    const mission = payload.id ? missionsService.update : missionsService.create;

    const callback = (newMission: Mission | null) => {
      closeMissionFormModal();
      resetFormCallback();

      if (newMission) {
        reload();
        notify(notifyTitle, t('successSave'), 'success');
      } else {
        notify(notifyTitle, t('saveFail'), 'danger');
      }
    };

    service(() => mission(payload), { callback, loader: setIsSubmitting });
  };

  const [missionSelected, setMissionSelected] = useState<OperationMission | null>(null);
  const showCancelTaskModal = (task: OperationMission) => setMissionSelected(task);
  const closeCancelTaskModal = () => setMissionSelected(null);
  const cancelMission = () => {
    if (!missionSelected) {
      return;
    }
    loader(true);

    const callback = (res: boolean) => {
      setMissionSelected(null);

      if (res) {
        reload();
        notify('Cancel Mission', t('successCancel'), 'success');
      } else {
        loader(false);
        notify('Cancel Mission', t('saveFail'), 'danger');
      }
    };

    service(() => missionsService.cancel(missionSelected.id), { callback, loader });
  };

  const handleShowEditMissionModal = (operationMission: OperationMission) => {
    service(() => missionsService.getMission(operationMission.id.toString()), {
      callback: (response) => {
        const { mission } = response;
        const state: OperationMissionForm = {
          missionNumber: mission.number,
          missionId: mission.id,
          startDate: new Date(mission.startDate),
          endDate: new Date(mission.endDate),
          country: mission.country,
          destinationCountry: mission.destinationCountry,
          objective: mission.objective,
          members: mission.memberList.map(
            (x) => ({ email: x.email, fullName: x.fullName } as User)
          ),
        };

        setMissionState(state);
        openMissionFormModal();
      },
    });
  };

  const cells: Cell<OperationMission>[] = [
    {
      name: 'number',
      column: 'number',
      value: (x) => x.number,
      render: (x, o) => <Link to={`/missions/${o.id}`}>{x}</Link>,
    },
    {
      name: 'type',
      column: 'type',
      value: (x) => x.type,
      render: (x) => <MissionType text={t(`missionTypesCode.${x}`, x)} type={x} />,
    },
    {
      name: 'startDate',
      column: 'startDate',
      value: (x) => customDateStr(x.startDate, 'dd/MM/yyy'),
    },
    { name: 'endDate', column: 'endDate', value: (x) => customDateStr(x.endDate, 'dd/MM/yyy') },
    {
      name: 'status',
      column: 'status',
      value: (x) => x.status,
      render: (x) => <MissionStatus text={t(`missionStatusCode.${x}`, x)} status={x} />,
    },
    { name: 'objective', column: 'objective', value: (x) => x.objective },
    {
      column: 'actions',
      sortDisabled: true,
      render: (_, o) =>
        o.type === 'ESGCOMPASS' &&
        o.status !== 'COMPLETED' &&
        o.status !== 'CANCELLED' &&
        canExecute ? (
          <div className="d-flex">
            <LinkButton
              htmlId={`edit_mission_${o.id}`}
              onClick={() => handleShowEditMissionModal(o)}
              icon="edit"
            />
            <UncontrolledTooltip
              className="tooltip-impact"
              target={`edit_mission_${o.id}`}
              placement="right"
              modifiers={tooltipPreventOverflowModifiers}
            >
              <p>Edit Mission</p>
            </UncontrolledTooltip>
            <LinkButton
              htmlId={`cancel_mission_${o.id}`}
              onClick={() => showCancelTaskModal(o)}
              icon="ban"
            />
            <UncontrolledTooltip
              className="tooltip-impact"
              target={`cancel_mission_${o.id}`}
              placement="right"
              modifiers={tooltipPreventOverflowModifiers}
            >
              <p>Cancel Mission</p>
            </UncontrolledTooltip>
          </div>
        ) : (
          <></>
        ),
    },
  ];

  return (
    <>
      <div className="details-bar">
        <div className="content">
          <h4>
            {data &&
              data.total > 0 &&
              t('showingMissions', { from: data.from, to: data.to, total: data.total })}
          </h4>
        </div>
        {canExecute && (
          <div>
            <Button
              key="btnAddMission"
              className="btn-secondary"
              text={t('createNewMission')}
              type="button"
              onClick={handleShowCreateNewMissionModal}
            />
            <div className="ml-auto" />
          </div>
        )}
      </div>
      <SpinnerArea loading={loading}>
        {data && data.total > 0 && (
          <>
            <Table
              data={data.list}
              cells={cells}
              keyFunc={(x) => x.id}
              sortData={filter}
              changeSort={changeSort}
            />
            <TablePagination
              pageSize={pageSize}
              pageNumber={filter.pageNumber}
              total={data.total}
              onChangePage={changePage}
            />
          </>
        )}
      </SpinnerArea>
      <CustomModal
        title={t(
          missionState && missionState.missionId
            ? 'missionModalTitleUpdate'
            : 'missionModalTitleCreate',
          {
            number,
          }
        )}
        visible={showCreateNewMissionModal}
        setVisible={setShowCreateNewMissionModal}
        customClass="modal-dialog--header-mission"
      >
        <MissionForm
          formState={missionState}
          handleSubmit={handleMissionFormSubmit}
          handleCancel={closeMissionFormModal}
          isSubmitting={isSubmitting}
        />
      </CustomModal>
      <ConfirmModal
        title={t('cancelMission')}
        buttonText={t('cancelMission')}
        visible={missionSelected != null}
        close={closeCancelTaskModal}
        action={() => cancelMission()}
      >
        {t('cancelMissionConfirmation')}
      </ConfirmModal>
    </>
  );
};

export default Missions;
